import { Flex, Button, Spinner } from '@chakra-ui/react'
import { useMemo, useState } from 'react'

import { InlineLoading } from 'components'

import { ICleanResponse, WebMeta } from 'api/types'

import { IBaseView } from 'interfaces/navigationPage.interface'
import { WidgetComponentPropsTypes } from 'interfaces/widget.interface'

import useTracking from 'tracking/useTracking'

import {
  type ArticleFiltersT,
  DefaultArticleFilters,
} from 'utils/useArticleFilters'
import { IModel } from 'interfaces/model.interface'

import { Article } from './Article'
import { ArticlesFilters } from './ArticlesFilters'
import _, { isEqual } from 'lodash'
import { useFilteredArticles } from './useFilteredArticles'
import { useWidgetData } from '..'

const Articles = ({
  data,
  url,
  title,
  keyNews,
  date,
  view,
  model,
  isLoading,
}: {
  data: ICleanResponse<any>[] | undefined
  url: string
  title: string
  keyNews: string | undefined
  date: string
  model: IModel<any, any>
  view: IBaseView<any, any>
  isLoading: boolean
}) => {
  const [tracking] = useTracking()
  const modelEndpoint = model.endpoint
  if (isLoading || !data) {
    return <InlineLoading />
  }
  function trackMediaClick(data: WebMeta[]) {
    if (!data) return
    const { date, publisher, title: articleTitle, url } = data?.[0]
    tracking.mediaSelection({
      section: view.airtableName,
      date: date || '',
      source: publisher,
      title: articleTitle,
      link: url,
      topic: '',
    })
  }

  return data.map((row, i) => {
    const parsedUrl = _.isObject(row[url]) ? (row[url] as any).link : row[url]
    const parsedTitle = _.isObject(row[title])
      ? (row[title] as any).title
      : row[title]

    return (
      <Article
        id={row.id}
        modelEndpoint={modelEndpoint}
        airtableBase={view.airtableBase}
        key={row.id}
        url={(parsedUrl || row.link) as string}
        date={row[date] as string}
        title={parsedTitle as string}
        tags={row?.tags as string[]}
        countries={row?.areasAlpha3 as string[]}
        isKeyNews={!!row[keyNews as string]}
        trackMediaClick={trackMediaClick}
      />
    )
  })
}

function WidgetComponentStandardArticle<TData extends string>({
  maxItems,
  model,
  view,
  url,
  title,
  keyNews,
  date,
}: WidgetComponentPropsTypes & CustomProp<TData>) {
  const [tracking] = useTracking()
  const { isLoading, data, canFetchMore, fetchMore, isFetchingNextPage } =
    useWidgetData({
      maxItems,
      model,
      view,
    })

  const modelEndpoint = model.endpoint
  if (isLoading || !data) {
    return <InlineLoading />
  }
  function trackMediaClick(data: WebMeta[]) {
    if (!data) return
    const { date, publisher, title: articleTitle, url } = data?.[0]
    tracking.mediaSelection({
      section: view.airtableName,
      date: date || '',
      source: publisher,
      title: articleTitle,
      link: url,
      topic: '',
    })
  }
  return (
    <>
      {data.map((row, i) => {
        const parsedUrl = _.isObject(row[url])
          ? (row[url] as any).link
          : row[url]
        const parsedTitle = _.isObject(row[title])
          ? (row[title] as any).title
          : row[title]

        return (
          <Article
            id={row.id}
            modelEndpoint={modelEndpoint}
            airtableBase={view.airtableBase}
            key={row.id}
            url={(parsedUrl || row.link) as string}
            date={row[date] as string}
            title={parsedTitle as string}
            tags={row?.tags as string[]}
            countries={row?.areasAlpha3 as string[]}
            isKeyNews={!!row[keyNews]}
            trackMediaClick={trackMediaClick}
          />
        )
      })}
      {canFetchMore && (
        <Flex p='1rem' w='100%' justifyContent='center'>
          <Button
            variant='outline'
            onClick={() => {
              tracking.loadMoreItems({ section: view.airtableName })
              fetchMore()
            }}
          >
            {isFetchingNextPage ? <Spinner /> : 'Load More'}
          </Button>
        </Flex>
      )}
    </>
  )
}

function WidgetComponentFilteredArticle<TData extends string>({
  maxItems,
  model,
  view,
  url,
  title,
  keyNews,
  date,
}: WidgetComponentPropsTypes & CustomProp<TData>) {
  const [tracking] = useTracking()
  const [filters, setFilters] = useState<ArticleFiltersT>(DefaultArticleFilters)
  function filtersCB(_filters: any) {
    setFilters(_filters)
  }
  const disableTracking = useMemo(
    () => isEqual(filters, DefaultArticleFilters),
    [filters]
  )
  const { isLoading, data, canFetchMore, fetchMore, isFetchingNextPage } =
    useFilteredArticles({ maxItems, model, view, filters, disableTracking })

  return (
    <>
      <ArticlesFilters filtersCB={filtersCB} view={view} />
      <Articles
        data={data}
        url={url}
        keyNews={keyNews}
        title={title}
        date={date}
        model={model}
        view={view}
        isLoading={isLoading}
      />
      {canFetchMore && (
        <Flex p='1rem' w='100%' justifyContent='center'>
          <Button
            variant='outline'
            onClick={() => {
              tracking.loadMoreItems({ section: view.airtableName })
              fetchMore()
            }}
          >
            {isFetchingNextPage ? <Spinner /> : 'Load More'}
          </Button>
        </Flex>
      )}
    </>
  )
}
type CustomProp<TData extends string> = {
  url: TData
  date: TData
  title: TData
  keyNews?: TData
  type?: 'filtered' | 'standard'
}
export default function WidgetComponentArticle<TData extends string>({
  maxItems,
  model,
  view,
  url,
  title,
  keyNews,
  date,
  type = 'standard',
}: WidgetComponentPropsTypes & CustomProp<TData>) {
  if (type === 'standard') {
    return (
      <WidgetComponentStandardArticle
        maxItems={maxItems}
        model={model}
        view={view}
        url={url}
        title={title}
        keyNews={keyNews}
        date={date}
      />
    )
  }
  if (type === 'filtered') {
    return (
      <WidgetComponentFilteredArticle
        maxItems={maxItems}
        model={model}
        view={view}
        url={url}
        title={title}
        keyNews={keyNews}
        date={date}
      />
    )
  }
  return null
}
